import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, PageHeader, Stepper, StepperNavFooter } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ArrowLeft } from 'lucide-react';

import { useRoleBuilder } from '../../hooks/useRoleBuilder';
import { useRoleMutations } from '../../hooks/useRoleMutations';
import { ROLE_BUILDER_STEP } from '../../utils/roleBuilderRules';
import { stepStateOf } from '../../utils/stepState';
import FullPageFrame from '../shared/FullPageFrame';
import DetailsStep from './DetailsStep';
import MembersStep from './MembersStep';
import PermissionsStep from './PermissionsStep';

const STEP_BODY = {
  [ROLE_BUILDER_STEP.DETAILS]: DetailsStep,
  [ROLE_BUILDER_STEP.PERMISSIONS]: PermissionsStep,
  [ROLE_BUILDER_STEP.MEMBERS]: MembersStep,
};

/**
 * Stepper, current step and nav footer of the New role builder. Create posts the wizard state;
 * on success it opens the new role, on failure the state is kept.
 *
 * @param {object} props
 * @param {string} props.basePath
 */
function RoleBuilderView({ basePath }) {
  const { t } = useTranslation('people-access');
  const navigate = useNavigate();
  const {
    stepIds,
    stepIndex,
    currentStep,
    isFirstStep,
    isLastStep,
    canGoNext,
    nameTaken,
    takenName,
    grantedCount,
    name,
    description,
    studioIds,
    grants,
    memberIds,
    goTo,
    goBack,
    goNext,
    reportNameConflict,
  } = useRoleBuilder();
  const { create } = useRoleMutations();
  const [submitting, setSubmitting] = useState(false);
  // The ref blocks a second click before React re-renders with `submitting`.
  const submittingRef = useRef(false);
  const listPath = `${basePath}/roles`;

  const steps = useMemo(
    () =>
      stepIds.map((id, index) => ({
        id,
        label: t(`roles.builder.steps.${id}`),
        state: stepStateOf(index, stepIndex),
      })),
    [stepIds, stepIndex, t],
  );

  const handleCancel = useCallback(() => navigate(listPath), [navigate, listPath]);
  const handleCreate = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    let result = { ok: false };
    try {
      result = await create({
        name: name.trim(),
        description: description.trim(),
        studios: studioIds,
        permissions: grants,
        member_ids: memberIds,
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
    if (result.ok) navigate(`${listPath}/${result.role.id}`);
    else if (result.status === 409) reportNameConflict(name);
  }, [create, name, description, studioIds, grants, memberIds, reportNameConflict, navigate, listPath]);

  let reason;
  if (!canGoNext) {
    if (currentStep === ROLE_BUILDER_STEP.DETAILS) {
      reason = nameTaken ? t('roles.builder.details.nameTaken', { name: takenName }) : t('roles.builder.nav.needName');
    } else {
      reason = grantedCount === 0 ? t('roles.builder.permissions.needOne') : undefined;
    }
  }

  const Body = STEP_BODY[currentStep];

  return (
    <div data-testid="page-role-new" className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <FullPageFrame>
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" leftIcon={ArrowLeft} aria-label={t('roles.builder.back')} onClick={handleCancel} />
          <PageHeader title={t('roles.builder.title')} subtitle={t('roles.builder.subtitle')} />
        </div>
        <Stepper ariaLabel={t('roles.builder.stepsAria')} steps={steps} onStepClick={goTo} />
        <Body />
      </FullPageFrame>
      <StepperNavFooter
        ariaLabel={t('roles.builder.navAria')}
        backLabel={isFirstStep ? t('roles.builder.nav.cancel') : t('roles.builder.nav.back')}
        backVariant={isFirstStep ? 'cancel' : 'back'}
        onBack={isFirstStep ? handleCancel : goBack}
        primaryLabel={isLastStep ? t('roles.builder.nav.create') : t('roles.builder.nav.next')}
        primaryIcon={isLastStep ? null : undefined}
        onPrimary={isLastStep ? handleCreate : goNext}
        primaryDisabled={!canGoNext}
        primaryDisabledReason={reason}
        primaryLoading={submitting}
        busy={submitting}
      />
    </div>
  );
}

export default RoleBuilderView;
